import { Check, Loader2, LogOut, Pencil, Search, User, UserPlus, Users, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { AsyncBoundary, EmptyState } from "@/components/data-state";
import { TeamNotice } from "@/components/team-notice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAsync } from "@/hooks/use-async";
import { useApiKey } from "@/hooks/use-auth";
import { ApiError, studentApi, type Classmate, type Me } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * A student's team: form it by adding classmates or alone, see who is on it, leave.
 *
 * There is no "join". Picking the first classmate makes the team; the person picked sees
 * "Alice added you" here and can leave with one click. That is the whole consent model,
 * and it is enough because a stranger cannot add themselves -- adding is something a
 * member does to others. A competition is run by teams, so a student competing alone
 * forms a team of one. Everything is subject to the course's policy (size cap,
 * open/closed), which the server enforces and this page explains.
 */
export function TeamRoute() {
  const apiKey = useApiKey();
  const me = useAsync(apiKey ? () => studentApi.me(apiKey) : null, [apiKey]);

  if (!apiKey) return <EmptyState title="Sign in to see your team" />;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">My team</h1>
      <AsyncBoundary state={me}>
        {(data) => <TeamPanel me={data} apiKey={apiKey} onChange={me.refetch} />}
      </AsyncBoundary>
    </div>
  );
}

function TeamPanel({ me, apiKey, onChange }: { me: Me; apiKey: string; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const policy = me.team_policy;
  const team = me.team;
  const full = team ? team.members.length >= policy.max_size : false;

  async function run(action: () => Promise<Me>, done: string) {
    setBusy(true);
    try {
      await action();
      onChange();
      toast.success(done);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 10000 });
    } finally {
      setBusy(false);
    }
  }

  if (me.role === "staff") {
    return <EmptyState title="Staff do not compete in teams" description="Your key works for dry runs; your submissions stay off the leaderboard." />;
  }

  return (
    <div className="space-y-6">
      <TeamNotice me={me} where="team" />

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        {team ? (
          <TeamCard me={me} busy={busy} onRename={(name) => run(() => studentApi.renameTeam(name, apiKey), "Renamed")} onLeave={() => run(() => studentApi.leaveTeam(apiKey), "You left the team")} />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="size-4" aria-hidden />
                You are not on a team
              </CardTitle>
              <CardDescription>
                Competitions are run by teams: a team shares its attempts and has one place on
                the leaderboard, and only teams can enter once team changes close. Add a
                classmate on the right and your team exists — or compete on your own.
              </CardDescription>
            </CardHeader>
            {policy.open ? (
              <CardContent>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => run(() => studentApi.formTeamAlone(apiKey), "Your team exists; it is just you for now")}
                >
                  <User className="size-4" aria-hidden />
                  Compete on my own
                </Button>
              </CardContent>
            ) : null}
          </Card>
        )}

        <div className="space-y-2">
          <h2 className="flex items-center gap-2 text-sm font-medium">
            <UserPlus className="size-4" aria-hidden />
            Add a teammate
          </h2>
          {!policy.open ? (
            <p className="text-muted-foreground text-xs">Closed.</p>
          ) : full ? (
            <p className="text-muted-foreground text-xs">
              Your team is full ({policy.max_size} people).
            </p>
          ) : (
            <ClassmatePicker apiKey={apiKey} busy={busy} onPick={(c) => run(() => studentApi.addTeammate(c.student_id, apiKey), `Added ${c.name || c.email}`)} />
          )}
          <p className="text-muted-foreground text-xs">
            Teams are up to {policy.max_size}. Whoever you add sees that you added them and can leave; nobody can add themselves to your team.
          </p>
        </div>
      </div>
    </div>
  );
}

function TeamCard({ me, busy, onRename, onLeave }: { me: Me; busy: boolean; onRename: (name: string) => void; onLeave: () => void }) {
  const team = me.team!;
  const open = me.team_policy.open;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(team.name);
  const [confirmLeave, setConfirmLeave] = useState(false);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          {editing ? (
            <form
              className="flex items-center gap-1.5"
              onSubmit={(e) => {
                e.preventDefault();
                if (name.trim() && name.trim() !== team.name) onRename(name.trim());
                setEditing(false);
              }}
            >
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 w-56" autoFocus maxLength={60} />
              <Button type="submit" size="icon-sm" variant="ghost" aria-label="Save name" disabled={busy}>
                <Check className="size-4" aria-hidden />
              </Button>
              <Button type="button" size="icon-sm" variant="ghost" aria-label="Cancel" onClick={() => { setName(team.name); setEditing(false); }}>
                <X className="size-4" aria-hidden />
              </Button>
            </form>
          ) : (
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="size-4" aria-hidden />
              {team.name}
              {open ? (
                <Button size="icon-sm" variant="ghost" aria-label="Rename team" onClick={() => setEditing(true)}>
                  <Pencil className="size-3.5" aria-hidden />
                </Button>
              ) : null}
            </CardTitle>
          )}
          <Badge variant="secondary">
            {team.members.length} / {me.team_policy.max_size}
          </Badge>
        </div>
        <CardDescription>Your team's leaderboard score is its best member's best run.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="divide-y rounded-md border">
          {team.members.map((m) => {
            const isMe = m.email === me.email;
            return (
              <li key={m.student_id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2 text-sm">
                <span className="font-medium">{m.name || m.email}</span>
                {isMe ? <Badge variant="outline" className="text-[10px]">you</Badge> : null}
                <span className="text-muted-foreground font-mono text-xs">{m.email}</span>
                <span className="text-muted-foreground ml-auto text-xs">
                  {m.added_by ? `added by ${m.added_by}` : m.joined_at ? "formed the team" : ""}
                  {m.joined_at ? ` · ${formatRelative(m.joined_at)}` : ""}
                </span>
              </li>
            );
          })}
        </ul>
        {open ? (
          <div className="flex items-center justify-end gap-2">
            {confirmLeave ? (
              <>
                <span className="text-muted-foreground text-xs">
                  {team.members.length === 1 ? "You are the last member; the team will be deleted." : "Leave the team?"}
                </span>
                <Button size="sm" variant="destructive" onClick={onLeave} disabled={busy}>
                  Leave
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmLeave(false)}>
                  Stay
                </Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => setConfirmLeave(true)}>
                <LogOut className="size-4" aria-hidden />
                Leave team
              </Button>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Search the course roster and pick someone. Those already on a team are shown, greyed, with where they are. */
function ClassmatePicker({ apiKey, busy, onPick }: { apiKey: string; busy: boolean; onPick: (c: Classmate) => void }) {
  const [query, setQuery] = useState("");
  const list = useAsync(() => studentApi.classmates(apiKey), [apiKey]);
  const needle = query.trim().toLowerCase();
  const rows = useMemo(
    () =>
      (list.data ?? [])
        .filter((c) => !needle || c.name.toLowerCase().includes(needle) || c.email.toLowerCase().includes(needle))
        .sort((a, b) => Number(b.available) - Number(a.available) || (a.name || a.email).localeCompare(b.name || b.email)),
    [list.data, needle],
  );

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" aria-hidden />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search classmates" className="pl-8" />
      </div>
      {list.loading ? (
        <p className="text-muted-foreground flex items-center gap-2 text-xs">
          <Loader2 className="size-3.5 animate-spin" aria-hidden /> Loading the roster…
        </p>
      ) : (
        <ul className="max-h-80 divide-y overflow-y-auto rounded-md border">
          {rows.length === 0 ? <li className="text-muted-foreground px-3 py-2 text-xs">Nobody matches.</li> : null}
          {rows.map((c) => (
            <li key={c.student_id} className={cn("flex items-center gap-2 px-3 py-1.5 text-sm", !c.available && "opacity-60")}>
              <div className="min-w-0 flex-1">
                <p className="truncate">{c.name || c.email}</p>
                <p className="text-muted-foreground truncate font-mono text-xs">{c.email}</p>
              </div>
              {c.available ? (
                <Button size="sm" variant="outline" onClick={() => onPick(c)} disabled={busy}>
                  <UserPlus className="size-3.5" aria-hidden />
                  Add
                </Button>
              ) : (
                <span className="text-muted-foreground text-xs">in {c.team_name}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
